import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "#/context/AuthProvider";
import { PageLoader } from "../common/PageLoader";

// Pages that are allowed even when MFA hasn't been enrolled yet.
const MFA_SETUP_EXEMPT = ["/account/security", "/change-password"];

type ProtectedRouteProps = {
  requiredRoles?: string[];
  requiredPermissions?: string[];
  requireAll?: boolean;
};

export function ProtectedRoute({
  requiredRoles = [],
  requiredPermissions = [],
  requireAll = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isBootstrapping, hasPermission, hasRole, mfaEnabled } =
    useAuth();
  const location = useLocation();

  if (isBootstrapping) {
    return <PageLoader label="Loading session..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // ── MFA enrollment gate ────────────────────────────────────────────────────
  // Only enforce at the top-level auth gate (no role/permission requirements on
  // this route instance), and only once mfaEnabled has been resolved (not null).
  const isTopLevelGate = requiredRoles.length === 0 && requiredPermissions.length === 0;
  const isMfaExemptPath = MFA_SETUP_EXEMPT.some((p) => location.pathname.startsWith(p));

  if (isTopLevelGate && mfaEnabled === false && !isMfaExemptPath) {
    return <Navigate to="/account/security?setup=required" replace />;
  }

  // ── Role / permission checks ───────────────────────────────────────────────
  const roleOk =
    requiredRoles.length === 0 ||
    requiredRoles.some((role) => hasRole(role));

  const permissionOk =
    requiredPermissions.length === 0 ||
    requiredPermissions.some((permission) => hasPermission(permission));

  const hasRequirements = requiredRoles.length > 0 || requiredPermissions.length > 0;

  const allowed = !hasRequirements
    ? true
    : requireAll
      ? roleOk && permissionOk
      : roleOk || permissionOk;

  if (!allowed) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
}
